
import { GlassCard } from "@/components/ui/glass-card";
import { Loader2, MonitorSmartphone, Activity, Calendar, Shield, AlertTriangle, Bug } from "lucide-react";
import { useCompactMode } from "@/hooks/useCompactMode";

interface VulnerabilityData {
  totalCVEs: number;
  activelyExploitedCount: number;
  outdatedDevices: number;
  isLoading: boolean;
}

interface DeviceStatusCardsProps {
  counts: {
    active: number;
    inactive: number;
    pending: number;
    total: number;
  };
  isLoading: boolean;
  statusFilter: string | null;
  onStatusClick: (status: string | null) => void;
  vulnerabilityData?: VulnerabilityData;
  staleCount?: number;
  staleThresholdDays?: number;
}

export const DeviceStatusCards = ({
  counts,
  isLoading,
  statusFilter,
  onStatusClick,
  mode = "full",
  vulnerabilityData,
  staleCount = 0,
  staleThresholdDays = 7,
}: DeviceStatusCardsProps & { mode?: "full" | "compact" | "hidden" }) => {
  const { compact: densityCompact } = useCompactMode();
  if (mode === "hidden") return null;

  // Only force compact when explicitly requested. Density preference alone
  // should NOT strip the descriptive insight text under each metric — that
  // text is the whole point of the cards on the Devices page.
  const isCompact = mode === "compact";
  // Keep density preference around in case we want to tighten paddings later
  void densityCompact;
  const complianceRate = counts.total > 0 ? Math.round((counts.active / counts.total) * 100) : 0;
  const pendingPercent = counts.total > 0 ? Math.round((counts.pending / counts.total) * 100) : 0;

  const cardA11y = (status: string | null) => {
    const selected = statusFilter === status;
    return {
      role: "button" as const,
      tabIndex: 0,
      "aria-pressed": selected,
      onKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onStatusClick(status);
        }
      },
    };
  };
  const focusRing =
    "min-h-[44px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background";

  return (
    <div className={`grid ${isCompact ? 'grid-cols-2' : 'grid-cols-1'} md:grid-cols-4 gap-${isCompact ? '3' : '6'} ${isCompact ? 'mb-4' : 'mb-8'} transition-all duration-500 ease-in-out`}>
      {/* All Devices */}
      <GlassCard 
        variant="subtle"
        hover={false}
        className={`${isCompact ? 'p-3' : 'p-6'} cursor-pointer ${focusRing} ${
          statusFilter === null ? 'ring-2 ring-black dark:ring-white' : ''
        }`}
        onClick={() => onStatusClick(null)}
        aria-label={`Show all devices, ${counts.total} total`}
        {...cardA11y(null)}
      >
        <div className={`flex justify-between items-start ${isCompact ? 'mb-1' : 'mb-2'} transition-all duration-500 ease-in-out`}>
          <h3 className={`${isCompact ? 'text-sm' : 'text-lg'} font-semibold text-slate-900 dark:text-white transition-all duration-500 ease-in-out`}>All Devices</h3>
          <div className={`transition-all duration-500 ease-in-out ${isCompact ? 'opacity-0 scale-0' : 'opacity-100 scale-100'}`}>
            {!isCompact && <MonitorSmartphone className="h-5 w-5 text-slate-600 dark:text-slate-400" />}
          </div>
        </div>
        {isLoading ? (
          <Loader2 className="h-6 w-6 animate-spin text-slate-600" />
        ) : (
          <>
            <p className={`${isCompact ? 'text-xl' : 'text-3xl'} font-bold text-slate-800 dark:text-white transition-all duration-500 ease-in-out`}>{counts.total}</p>
            <div className={`transition-all duration-500 ease-in-out ${isCompact ? 'opacity-0 max-h-0 overflow-hidden' : 'opacity-100 max-h-20'}`}>
              {!isCompact && <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">Enrolled endpoints</p>}
              {!isCompact && counts.total > 0 && staleCount > 0 && (
                <div className="flex items-center gap-1 mt-2 text-sm text-warning">
                  <AlertTriangle className="h-4 w-4" />
                  <span>{staleCount} device{staleCount !== 1 ? 's' : ''} not checked in for {staleThresholdDays}+ days</span>
                </div>
              )}
              {!isCompact && counts.total > 0 && staleCount === 0 && (
                <div className="flex items-center gap-1 mt-2 text-sm text-success">
                  <Activity className="h-4 w-4" />
                  <span>All devices checked in recently</span>
                </div>
              )}
            </div>
          </>
        )}
      </GlassCard>
      
      {/* Healthy */}
      <GlassCard 
        variant="subtle"
        hover={false}
        className={`${isCompact ? 'p-3' : 'p-6'} cursor-pointer ${focusRing} ${
          statusFilter === 'active' ? 'ring-2 ring-success' : ''
        }`}
        onClick={() => onStatusClick('active')}
        aria-label={`Filter healthy devices, ${counts.active} ${counts.active === 1 ? 'device' : 'devices'}`}
        {...cardA11y('active')}
      >
        <div className={`flex justify-between items-start ${isCompact ? 'mb-1' : 'mb-2'} transition-all duration-500 ease-in-out`}>
          <h3 className={`${isCompact ? 'text-sm' : 'text-lg'} font-semibold text-slate-900 dark:text-white transition-all duration-500 ease-in-out`}>Healthy</h3>
          <div className={`transition-all duration-500 ease-in-out ${isCompact ? 'opacity-0 scale-0' : 'opacity-100 scale-100'}`}>
            {!isCompact && <Activity className="h-5 w-5 text-success" />}
          </div>
        </div>
        {isLoading ? (
          <Loader2 className="h-6 w-6 animate-spin text-success" />
        ) : (
          <>
            <p className={`${isCompact ? 'text-xl' : 'text-3xl'} font-bold text-success transition-all duration-500 ease-in-out`}>{counts.active}</p>
            <div className={`transition-all duration-500 ease-in-out ${isCompact ? 'opacity-0 max-h-0 overflow-hidden' : 'opacity-100 max-h-20'}`}>
              {!isCompact && <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">Devices in compliance</p>}
              {!isCompact && (
                <div className="flex items-center gap-1 mt-2 text-sm text-success">
                  <Activity className="h-4 w-4" />
                  <span>{complianceRate}% compliance rate{complianceRate >= 80 ? ' — above target' : complianceRate > 0 ? ' — below 80% target' : ''}</span>
                </div>
              )}
            </div>
          </>
        )}
      </GlassCard>
      
      {/* Pending Actions */}
      <GlassCard 
        variant="subtle"
        hover={false}
        className={`${isCompact ? 'p-3' : 'p-6'} cursor-pointer ${focusRing} ${
          statusFilter === 'pending' ? 'ring-2 ring-warning' : ''
        }`}
        onClick={() => onStatusClick('pending')}
        aria-label={`Filter pending devices, ${counts.pending} ${counts.pending === 1 ? 'device' : 'devices'}`}
        {...cardA11y('pending')}
      >
        <div className={`flex justify-between items-start ${isCompact ? 'mb-1' : 'mb-2'} transition-all duration-500 ease-in-out`}>
          <h3 className={`${isCompact ? 'text-sm' : 'text-lg'} font-semibold text-slate-900 dark:text-white transition-all duration-500 ease-in-out`}>Pending Actions</h3>
          <div className={`transition-all duration-500 ease-in-out ${isCompact ? 'opacity-0 scale-0' : 'opacity-100 scale-100'}`}>
            {!isCompact && <Calendar className="h-5 w-5 text-warning" />}
          </div>
        </div>
        {isLoading ? (
          <Loader2 className="h-6 w-6 animate-spin text-warning" />
        ) : (
          <>
            <p className={`${isCompact ? 'text-xl' : 'text-3xl'} font-bold text-warning transition-all duration-500 ease-in-out`}>{counts.pending}</p>
            <div className={`transition-all duration-500 ease-in-out ${isCompact ? 'opacity-0 max-h-0 overflow-hidden' : 'opacity-100 max-h-20'}`}>
              {!isCompact && <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">Devices awaiting setup</p>}
              {!isCompact && counts.pending > 0 && (
                <div className="flex items-center gap-1 mt-2 text-sm text-warning">
                  <AlertTriangle className="h-4 w-4" />
                  <span>{pendingPercent}% of fleet awaiting enrollment</span>
                </div>
              )}
              {!isCompact && counts.pending === 0 && (
                <div className="flex items-center gap-1 mt-2 text-sm text-success">
                  <Activity className="h-4 w-4" />
                  <span>All devices enrolled</span>
                </div>
              )}
            </div>
          </>
        )}
      </GlassCard>
      
      {/* Inactive (lifecycle) */}
      <GlassCard 
        variant="subtle"
        hover={false}
        className={`${isCompact ? 'p-3' : 'p-6'} cursor-pointer ${focusRing} ${
          statusFilter === 'inactive' ? 'ring-2 ring-destructive' : ''
        }`}
        onClick={() => onStatusClick('inactive')}
        aria-label={`Filter inactive devices, ${counts.inactive} ${counts.inactive === 1 ? 'device' : 'devices'}`}
        {...cardA11y('inactive')}
      >
        <div className={`flex justify-between items-start ${isCompact ? 'mb-1' : 'mb-2'} transition-all duration-500 ease-in-out`}>
          <h3 className={`${isCompact ? 'text-sm' : 'text-lg'} font-semibold text-slate-900 dark:text-white transition-all duration-500 ease-in-out`}>Inactive</h3>
          <div className={`transition-all duration-500 ease-in-out ${isCompact ? 'opacity-0 scale-0' : 'opacity-100 scale-100'}`}>
            {!isCompact && <Shield className="h-5 w-5 text-destructive" />}
          </div>
        </div>
        {isLoading ? (
          <Loader2 className="h-6 w-6 animate-spin text-destructive" />
        ) : (
          <>
            <p className={`${isCompact ? 'text-xl' : 'text-3xl'} font-bold text-destructive transition-all duration-500 ease-in-out`}>{counts.inactive}</p>
            <div className={`transition-all duration-500 ease-in-out ${isCompact ? 'opacity-0 max-h-0 overflow-hidden' : 'opacity-100 max-h-20'}`}>
              {!isCompact && <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">Unenrolled or not checked in</p>}
              {!isCompact && vulnerabilityData && !vulnerabilityData.isLoading && (
                <>
                  {vulnerabilityData.outdatedDevices > 0 && (
                    <div className="flex items-center gap-1 mt-2 text-sm text-destructive">
                      <Bug className="h-4 w-4" />
                      <span>{vulnerabilityData.outdatedDevices} device{vulnerabilityData.outdatedDevices !== 1 ? 's' : ''} with known CVEs</span>
                    </div>
                  )}
                  {vulnerabilityData.activelyExploitedCount > 0 && (
                    <div className="flex items-center gap-1 mt-1 text-sm text-destructive">
                      <AlertTriangle className="h-4 w-4" />
                      <span>{vulnerabilityData.activelyExploitedCount} actively exploited CVE{vulnerabilityData.activelyExploitedCount !== 1 ? 's' : ''}</span>
                    </div>
                  )}
                  {vulnerabilityData.outdatedDevices === 0 && vulnerabilityData.totalCVEs > 0 && (
                    <div className="flex items-center gap-1 mt-2 text-sm text-success">
                      <Shield className="h-4 w-4" />
                      <span>Fleet is up to date — no known CVE exposure</span>
                    </div>
                  )}
                </>
              )}
              {!isCompact && (!vulnerabilityData || vulnerabilityData.isLoading) && (
                <div className="flex items-center gap-1 mt-2 text-sm text-destructive">
                  <Shield className="h-4 w-4" />
                  <span>Security policy violations</span>
                </div>
              )}
            </div>
          </>
        )}
      </GlassCard>
    </div>
  );
};
